import React, { useEffect, useMemo, useState } from 'react';

import type { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material';
import { Autocomplete, Typography } from '@mui/material';
import { AlineAutocompleteInput, AlineCheckbox } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { partySearchLimit, usePartySearch } from 'src/hooks';

import { PartyListboxPaginationProvider, VirtualizedPartyListbox } from './VirtualizedPartyListbox';

interface NewPartyOption {
  __typename: 'NewParty';
  name: string;
  optionText: string;
}
export type ExistingPartyOption = Pick<Apollo.Party, 'id' | '__typename' | 'name'>;
type PartyOption = NewPartyOption | ExistingPartyOption;

export interface PartiesAutocompleteProps<Multiple extends boolean> extends Omit<
  AutocompleteProps<PartyOption, Multiple, false, false>,
  'options' | 'renderInput'
> {
  checkboxOptions?: boolean;
  filterPred?: (party: ExistingPartyOption) => boolean;
  inputClassName?: string;
  placeholder?: string;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  suggestNewOption?: boolean;
}

export function PartiesAutocomplete<Multiple extends boolean = false>({
  blurOnSelect = false,
  checkboxOptions = false,
  filterPred,
  inputClassName,
  onInputChange,
  placeholder = 'Search parties...',
  renderInput,
  renderOption,
  slotProps,
  suggestNewOption = false,
  ...rest
}: PartiesAutocompleteProps<Multiple>): JSX.Element {
  const callerListboxProps =
    typeof slotProps?.listbox === 'object' && slotProps.listbox !== null ? slotProps.listbox : {};
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
  const { hasMore, loadMore, loading, parties, setSearch } = usePartySearch({
    skip: !autocompleteOpen,
  });
  const sortedOptions = useMemo(() => {
    if (!filterPred) return parties;

    return parties.filter(filterPred);
  }, [filterPred, parties]);

  /**
   * `filterPred` removes options client-side (e.g. parties already assigned), so
   * a full server page can collapse to too few rows to scroll — which would
   * strand pagination since scrolling is what triggers loadMore. Keep pulling
   * pages until there are at least a page's worth of visible options or the
   * server is exhausted.
   */
  useEffect(() => {
    if (!filterPred || !hasMore || loading) return;
    if (sortedOptions.length < partySearchLimit) loadMore();
  }, [filterPred, hasMore, loadMore, loading, sortedOptions.length]);

  const effectiveRenderOption =
    renderOption ?? (checkboxOptions ? renderPartyOptionWithCheckbox : undefined);

  return (
    <PartyListboxPaginationProvider hasMore={hasMore} loadMore={loadMore} loading={loading}>
      <Autocomplete<PartyOption, Multiple, false, false>
        disableListWrap
        fullWidth
        openOnFocus
        blurOnSelect={blurOnSelect}
        filterOptions={(options, params) => {
          /**
           * Options are already filtered server-side by the search term, so the
           * list is passed through unchanged. We only append the "create new
           * party" suggestion when the typed name does not already exist — and
           * only once all matches for the term are loaded (`!hasMore`), otherwise
           * an existing party on a not-yet-loaded page could trigger a false
           * "Add new" and let the user create a duplicate.
           */
          if (!suggestNewOption || hasMore) return options;

          const isSuggestingNewOption =
            params.inputValue !== '' &&
            options.every(option => option.name.toLowerCase() !== params.inputValue.toLowerCase());
          if (!isSuggestingNewOption) return options;

          return [
            ...options,
            {
              __typename: 'NewParty',
              name: params.inputValue,
              optionText: `Add "${params.inputValue}"`,
            } satisfies NewPartyOption,
          ];
        }}
        getOptionLabel={option => {
          switch (option.__typename) {
            case 'NewParty':
              return option.optionText;
            default:
              return option.name;
          }
        }}
        isOptionEqualToValue={(option, value) => {
          if (option.__typename === 'NewParty' || value.__typename === 'NewParty') return false;

          return option.id === value.id;
        }}
        loading={loading}
        open={autocompleteOpen}
        options={sortedOptions}
        renderInput={params => {
          if (renderInput) return renderInput(params);

          return (
            <DefaultRenderInput
              inputClassName={inputClassName}
              params={params}
              placeholder={placeholder}
            />
          );
        }}
        renderOption={effectiveRenderOption}
        onClose={() => {
          setAutocompleteOpen(false);
          setSearch('');
        }}
        onInputChange={(event, value, reason) => {
          setSearch(value);
          onInputChange?.(event, value, reason);
        }}
        onOpen={() => setAutocompleteOpen(true)}
        {...rest}
        slotProps={{
          ...slotProps,
          listbox: {
            'data-cy': 'parties-autocomplete-options',
            // Strip the default listbox padding so react-window's rows line up.
            sx: { '& > ul': { margin: 0, padding: 0 }, padding: 0 },
            ...callerListboxProps,
          },
        }}
        slots={{ listbox: VirtualizedPartyListbox }}
      />
    </PartyListboxPaginationProvider>
  );
}

function renderPartyOptionWithCheckbox(
  props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
  option: PartyOption,
  { selected }: { selected: boolean }
): React.ReactNode {
  const { key, ...optionProps } = props;

  return (
    <li key={key} {...optionProps}>
      {option.__typename !== 'NewParty' && (
        <AlineCheckbox checked={selected} size="small" style={{ marginRight: 8, padding: 0 }} />
      )}
      <Typography noWrap>
        {option.__typename === 'NewParty' ? option.optionText : option.name}
      </Typography>
    </li>
  );
}

interface DefaultRenderInputProps {
  params: AutocompleteRenderInputParams;
  inputClassName?: string;
  placeholder?: string;
}

function DefaultRenderInput({
  inputClassName,
  params,
  placeholder,
}: DefaultRenderInputProps): JSX.Element {
  return (
    <AlineAutocompleteInput
      {...params}
      inputClassName={inputClassName}
      inputProps={{
        ...params.slotProps.htmlInput,
        ['data-cy']: 'contact-type-autocomplete-input',
      }}
      placeholder={placeholder}
    />
  );
}
